import { useEffect, useRef } from 'react';
import i18n from '@/i18n';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import type { UploadedAttachment } from '@/lib/uploader';
import type { Message } from '@/types/api';
import { useDataStore } from '@/stores/dataStore';
import { releaseSlots, type UploadSlot } from '@/components/contentPane/useComposerUploads';
import {
  createChannelSendQueue,
  createFailedSendBox,
  mergeFailedParts,
  type FailedSend,
} from '@/components/contentPane/composerSend';
import type { DataState } from '@/stores/dataStore';

/** A failed channel message waiting for its channel's Composer. */
export type FailedChannelSend = FailedSend<UploadedAttachment, UploadSlot, Message>;

/** The app's hand-back box for failed sends, keyed by channel id. */
export const failedSends = createFailedSendBox<FailedChannelSend>();

/**
 * The app's single send queue (channel messages, voice notes, thread
 * comments): one line per key, shared by every Composer / thread panel.
 */
export const sendQueue = createChannelSendQueue<UploadedAttachment, UploadSlot, Message, Message>({
  confirm: (channelId, tmpId, real) => useDataStore.getState().replaceMessage(channelId, tmpId, real),
  rollback: (channelId, tmpId) => useDataStore.getState().removeMessage(channelId, tmpId),
  release: releaseSlots,
  onError: (err) => toast.error(...errToToast(err, i18n.t('composer.sendError'))),
  handBack: (channelId, failed) => {
    failedSends.put(channelId, failed);
    // The channel may already be gone (deleted while offline, dropped by the
    // ready snapshot before the send failed): its Composer will never come,
    // and watchGoneChannels only sees channels that disappear later.
    if (!channelKnown(useDataStore.getState(), channelId)) discardFailedSends(channelId);
  },
});

/** The channel is in the store: a guild channel we can see, or a DM. */
export function channelKnown(s: Pick<DataState, 'guilds' | 'dmChannels'>, channelId: string): boolean {
  if (s.dmChannels[channelId]) return true;
  return Object.values(s.guilds).some((g) => g.channels.some((c) => c.id === channelId));
}

/**
 * A channel whose Composer will not come back (deleted, hidden from us, the
 * server left, signed out): drop what waits for it, free the attachment
 * thumbnails, and say so — with a button to copy the text (a click is the
 * gesture the clipboard needs), so it is not lost silently.
 *
 * A channel that only became read-only keeps its hand-back: it comes back
 * if sending is allowed again.
 */
export function discardFailedSends(channelId: string): void {
  const items = failedSends.take(channelId);
  if (items.length === 0) return;
  const merged = mergeFailedParts(items);
  releaseSlots(merged.slots);
  const text = merged.content.trim();
  if (!text) {
    toast.error(i18n.t('composer.sendLost'));
    return;
  }
  toast.error(i18n.t('composer.sendLost'), {
    description: text.length > 120 ? `${text.slice(0, 120)}…` : text,
    duration: 20_000,
    action: {
      label: i18n.t('composer.copyLostText'),
      onClick: () => {
        navigator.clipboard.writeText(text).then(
          () => toast.success(i18n.t('messages.textCopied')),
          () => toast.error(i18n.t('messages.copyError')),
        );
      },
    },
  });
}

/** Watch the store for channels that disappear while a hand-back waits. */
export function watchGoneChannels(
  subscribe: (listener: (s: DataState, prev: DataState) => void) => () => void,
): () => void {
  return subscribe((s, prev) => {
    for (const id of failedSends.keys()) {
      if (channelKnown(prev, id) && !channelKnown(s, id)) discardFailedSends(id);
    }
  });
}

watchGoneChannels((listener) => useDataStore.subscribe(listener));

/**
 * Run `apply` with everything that failed in `channelId`: once at mount (a
 * send may have failed while another channel was open) and again whenever a
 * send of this channel fails while mounted. Items are taken from the box,
 * so a later Composer of the same channel never applies them twice.
 */
export function useFailedSendHandBack(channelId: string, apply: (items: FailedChannelSend[]) => void): void {
  const applyRef = useRef(apply);
  applyRef.current = apply;
  useEffect(() => {
    const takeAll = () => {
      const items = failedSends.take(channelId);
      if (items.length > 0) applyRef.current(items);
    };
    takeAll();
    return failedSends.subscribe((id) => {
      if (id === channelId) takeAll();
    });
  }, [channelId]);
}
